import {
  BooleanInput,
  SimpleForm,
  TextInput,
  required,
  RadioButtonGroupInput,
  Loading,
  useRecordContext,
  FormDataConsumer,
  SaveButton,
  Toolbar,
  ToolbarProps,
  SelectInput,
} from 'react-admin';
import {
  CampaignLineItemsSelectInput,
  CurrentLineItemDateInputGroup,
  FormDivider,
  FormHeader,
  FormSectionHeader,
} from './form-components';
import { CreativesFormProps } from './fma-creatives';
import { ClicksCreativeTargetingType, SponsoredContentTeaser } from 'src/generated/client';
import { titleize } from 'inflection';
import { AutocompleteInput, TargetListAutocompleteInput } from './shared';
import { validateContentId } from './form-utils';
import { range } from 'lodash';
import { useNativeLineItem } from './hooks/useNativeLineItem';

const targetingTypeChoices = (
  Object.keys(ClicksCreativeTargetingType) as Array<keyof typeof ClicksCreativeTargetingType>
).map(targetingTypeKey => {
  const targetingType = ClicksCreativeTargetingType[targetingTypeKey];

  return {
    id: targetingType,
    name: titleize(targetingType),
  };
});

const SponsoredContentTeaserToolbar = (props: ToolbarProps) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export const SponsoredContentTeaserForm = ({ type, ...rest }: CreativesFormProps) => {
  const record = useRecordContext();

  // Resolve the parent line item from native data for BOTH id forms — the
  // Placements.IO API shut off at contract end, so the legacy 'line_items'
  // proxy can no longer serve imported line items. On EDIT a saved row prefers
  // its native lineItemId FK; on CREATE only placementsLineItemId is seeded
  // (native UUID or legacy numeric id, which useNativeLineItem translates via
  // externalPioId).
  const { lineItem, isLoading, error } = useNativeLineItem(record.lineItemId ?? record.placementsLineItemId);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <div>Error loading data: {error.toString()}</div>;
  }

  if (!lineItem) return null;

  const campaignId = lineItem.campaignId;

  const defaultValues: SponsoredContentTeaser = {
    ...rest.defaultValues,
    startsAt: lineItem.startDate,
    endsAt: lineItem.endDate,
  };

  return (
    <SimpleForm
      {...rest}
      defaultValues={defaultValues}
      toolbar={<SponsoredContentTeaserToolbar />}
      mode="onBlur"
      reValidateMode="onBlur"
    >
      <FormHeader>{`${type} Sponsored Content Teaser, Targeting, and Pacing`}</FormHeader>

      <CampaignLineItemsSelectInput campaignId={campaignId} />

      <CreativeDetailsSection />
      <FormDivider />

      <TargetingDetailsSection />
      <FormDivider />

      <PacingSection />
    </SimpleForm>
  );
};

const CreativeDetailsSection = () => (
  <>
    <FormSectionHeader>Creative Details</FormSectionHeader>
    <BooleanInput source="isApproved" label="Approved" defaultValue={false} validate={validateIsApproved} />
    <TextInput label="Title" source="title" resettable fullWidth multiline validate={required()} />
    <TextInput label="Company / Brand Name" source="companyName" fullWidth resettable validate={required()} />
    <CurrentLineItemDateInputGroup />
    <TextInput label="Content ID" source="contentId" fullWidth resettable validate={[required(), validateContentId]} />
  </>
);

const TargetingDetailsSection = () => (
  <>
    <FormSectionHeader>Targeting Details</FormSectionHeader>
    <RadioButtonGroupInput
      source="targetingType"
      choices={targetingTypeChoices}
      defaultValue={ClicksCreativeTargetingType.TargetList}
    />

    <FormDataConsumerWithTargeting />
  </>
);

const FormDataConsumerWithTargeting = () => (
  <FormDataConsumer>
    {({ formData }) => {
      const targetingType = formData.targetingType;

      if (targetingType === ClicksCreativeTargetingType.TargetList) {
        return <TargetListAutocompleteInput source="targetListIdsList" />;
      } else if (targetingType === ClicksCreativeTargetingType.RunOfSpecialty) {
        return (
          <>
            <AutocompleteInput resource="specialties" source="specialtyIdsList" validate={validateRunOfSpecialty} />
            <AutocompleteInput resource="professions" source="professionIdsList" />
          </>
        );
      }
      return null;
    }}
  </FormDataConsumer>
);

export const validateRunOfSpecialty = (value: number[], allValues: SponsoredContentTeaser) => {
  if (allValues.targetingType === ClicksCreativeTargetingType['RunOfSpecialty'] && value.length === 0) {
    return 'If Run Of Specialty targeting is selected, you must select at least one specialty';
  }
  return undefined;
};


const validateIsApproved = (value: boolean, allValues: SponsoredContentTeaser) => {
  if (value && !allValues.contentId) {
    return 'Content ID must be valid to approve the teaser';
  }
  return undefined;
};

const PacingSection = () => (
  <>
    <FormSectionHeader>Pacing</FormSectionHeader>
    <SelectInput
      source="priority"
      validate={[required()]}
      defaultValue={8}
      helperText="Between 1 (Highest) and 16 (Lowest)"
      choices={range(1, 17).map(value => {
        return {
          id: value,
          name: `${value}`,
        };
      })}
    />
  </>
);
